import { DocumentIcon, TrashIcon } from '@heroicons/react/24/solid';
import { useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useParams } from 'react-router-dom';

import Button, { ButtonVariant } from 'components/Button/Button';
import CTAModal from 'components/CTAModal/CTAModal';
import ContentWrapper from 'components/ContentWrapper/ContentWrapper';
import DisplayItem from 'components/DisplayItem/DisplayItem';
import Dropdown from 'components/Dropdown/Dropdown';
import Panel from 'components/Panel/Panel';
import Placeholder from 'components/Placeholder/Placeholder';
import useDeleteDocumentRequirement from 'hooks/useDeleteDocumentRequirement';
import useDeleteSupplementalMaterials from 'hooks/useDeleteSupplementalMaterials';
import useGetSupplementalMaterials from 'hooks/useGetSupplementalMaterials';
import useNotification from 'hooks/useNotification';
import { t } from 'locales/i18n';

import ApplicantDownloadForm from './ApplicantDownloadForm';
import ApplicantUploadForm from './ApplicantUploadForm';

enum DeleteAction {
  SupplementalMaterials = 'supplementalMaterials',
  DocumentRequirement = 'documentRequirement',
}

const SupplementalMaterials = () => {
  const [isUploadFormOpen, setIsUploadFormOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [documentId, setDocumentId] = useState('');
  const [deleteAction, setDeleteAction] = useState('');

  const { interviewUid } = useParams();
  const { showErrorNotification, showSuccessNotification } = useNotification();
  const queryClient = useQueryClient();

  const {
    data,
    isLoading: isSupplementalMaterialsLoading,
    isFetching,
  } = useGetSupplementalMaterials(interviewUid ?? '');
  const { deleteDocumentRequirement, isLoading: isDeleteLoading } =
    useDeleteDocumentRequirement();
  const {
    deleteSupplementalMaterials,
    isLoading: isDeleteSuppleMentalMaterialsLoading,
  } = useDeleteSupplementalMaterials();

  const resetModal = useCallback(() => {
    setIsModalOpen(false);
    setDocumentId('');
  }, []);

  const handleCreateApplicantUpload = () => {
    setIsUploadFormOpen(true);
  };

  const handleDeleteDocumentRequirement = async () => {
    if (!documentId || !interviewUid) {
      return;
    }

    await deleteDocumentRequirement(
      {
        documentId,
        interviewUid,
      },
      {
        onSuccess: () => {
          resetModal();
          showSuccessNotification(
            t('Supplemental material deleted'),
            t('Applicant upload successfully deleted')
          );

          queryClient.invalidateQueries({
            queryKey: ['getSupplementalMaterials', interviewUid],
          });

          queryClient.invalidateQueries({
            queryKey: ['getAssessmentStepDetails', interviewUid],
          });
        },
        onError: (error) => {
          showErrorNotification(
            t('An error occurred while deleting the document requirement'),
            error.message
          );
        },
      }
    );
  };

  const handleDeleteSupplementalMaterials = async () => {
    if (!interviewUid) return;
    await deleteSupplementalMaterials(
      {
        interviewUid,
      },
      {
        onSuccess: () => {
          showSuccessNotification(
            t('Supplemental material deleted'),
            t('Applicant download successfully deleted')
          );

          queryClient.invalidateQueries({
            queryKey: ['getSupplementalMaterials', interviewUid],
          });
          queryClient.invalidateQueries({
            queryKey: ['getAssessmentStepDetails', interviewUid],
          });
        },
        onError: (error) => {
          showErrorNotification(
            t('An error occurred while deleting the supplemental materials'),
            error.message
          );
        },
      }
    );
    setIsModalOpen(false);
  };

  return (
    <ContentWrapper title={t('Supplemental Materials')}>
      <Panel panelClassName="mt-6 space-y-8">
        <div className="relative w-full">
          <div className="prose-title-h5 pb-1 text-kira-slate-900">
            {t('Applicant Download')}
          </div>
          <div className="prose-body-sm-reg pb-4 text-kira-slate-800">
            {t(
              'Require applicants to download one or more files during Check In. Multiple files must be zipped together.'
            )}
          </div>
          {!isSupplementalMaterialsLoading || !isFetching ? (
            <div data-testId="materialDownloadCard">
              {data?.supplemental_materials_name ? (
                <DisplayItem
                  key={data.supplemental_materials_name}
                  title={data.supplemental_materials_name}
                >
                  <Dropdown
                    options={[
                      {
                        label: t('Delete'),
                        icon: <TrashIcon className="h-3.5 w-3.5" />,
                        onClick: () => {
                          setIsModalOpen(true);
                          setDeleteAction(DeleteAction.SupplementalMaterials);
                        },
                      },
                    ]}
                  />
                </DisplayItem>
              ) : (
                <ApplicantDownloadForm />
              )}
            </div>
          ) : (
            <Skeleton height={300} />
          )}
        </div>
        <div className="relative w-full">
          <div className="prose-title-h5 pb-1 text-kira-slate-900">
            {t('Applicant Uploads')}
          </div>
          <div className="prose-body-sm-reg pb-4 text-kira-slate-800">
            {t(
              'Require applicants to upload one or more files during Check In.'
            )}
          </div>
          {!isSupplementalMaterialsLoading || !isFetching ? (
            <div>
              {!data?.document_requirements.length && !isUploadFormOpen && (
                <Placeholder
                  icon={<DocumentIcon />}
                  title={t('No material added yet')}
                >
                  <Button
                    variant={ButtonVariant.SECONDARY}
                    buttonText={t('Create Applicant Upload')}
                    onClick={handleCreateApplicantUpload}
                    data-testid="applicantUpload"
                  />
                </Placeholder>
              )}
            </div>
          ) : (
            <Skeleton height={300} />
          )}
          <div className="grid gap-4" data-testId="materialUploadCard">
            {data?.document_requirements &&
              [...data.document_requirements].reverse().map(({ id, name }) => (
                <DisplayItem key={id} title={name}>
                  <Dropdown
                    options={[
                      {
                        label: t('Delete'),
                        icon: <TrashIcon className="h-3.5 w-3.5" />,
                        onClick: () => {
                          setDocumentId(id);
                          setIsModalOpen(true);
                          setDeleteAction(DeleteAction.DocumentRequirement);
                        },
                      },
                    ]}
                  />
                </DisplayItem>
              ))}
            {isUploadFormOpen && (
              <ApplicantUploadForm onClose={() => setIsUploadFormOpen(false)} />
            )}
          </div>
        </div>
        {isUploadFormOpen || data?.document_requirements.length ? (
          <div className="flex items-start">
            <Button
              variant={ButtonVariant.SECONDARY}
              buttonText={t('Create Applicant Upload')}
              onClick={handleCreateApplicantUpload}
              disabled={isUploadFormOpen}
            />
          </div>
        ) : null}
        <CTAModal
          isOpen={isModalOpen}
          setIsOpen={setIsModalOpen}
          title={t('Delete Supplemental Material')}
          description={t(
            "Deleting a supplemental material is permanent and can't be undone."
          )}
          confirmation={t('Are you sure you want to delete it?')}
          primaryButtonText={t('Delete')}
          isLoading={isDeleteLoading || isDeleteSuppleMentalMaterialsLoading}
          action={
            deleteAction === DeleteAction.SupplementalMaterials
              ? handleDeleteSupplementalMaterials
              : handleDeleteDocumentRequirement
          }
          buttonDataTestId="modalDelete"
        />
      </Panel>
    </ContentWrapper>
  );
};
export default SupplementalMaterials;
